import * as React from 'react';
import { initialFormData } from './initialFormData';
import { MoedaVendaEnum } from '../../types/enums/moeda-venda.enum';
import { MetodoPagamentoVendaEnum } from '../../types/enums/metodo-pagamento.enum';
import { emitFacebookEvent } from '../../utils/emit-facebook-event';
import { useDebounce } from '../useDebounce';
import { useCarrinhoAbandonadoTracking } from '../useCarrinhoAbandonadoTracking';
import {
  FormDataShape,
  StripeState,
  UseCheckoutControllerParams,
  UseCheckoutControllerReturn,
} from './useCheckoutController.types';
import { useDispatch, useSelector } from 'react-redux';
import { setFormField } from '../../redux/slices/client-data.slice';
import { selectPurchaseFormData } from '../../redux/selectors/client-data.selectors';
import { useCallback, useEffect } from 'react';
import { parseAutofillParams } from '../../utils/url-autofill.util';
import { getAllUrlParams } from '../../utils/get-url-params';
import { CreatePurchaseContract } from '../../types/create-purchase-contract';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

export function useCheckoutController(
  params: UseCheckoutControllerParams & {
    stripeNumberRef?: React.RefObject<{ focus: () => void }>;
    stripeExpiryRef?: React.RefObject<{ focus: () => void }>;
    stripeCvcRef?: React.RefObject<{ focus: () => void }>;
    stripeState?: StripeState;
  },
): UseCheckoutControllerReturn {
  const form = useSelector(selectPurchaseFormData);
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const {
    checkout,
    paymentMethod,
    onSubmit,
    mpCardRef,
    t,
    alert,
    eventId,
    stripeNumberRef,
    stripeExpiryRef,
    stripeCvcRef,
    stripeState,
    onValidationError,
  } = params as any;

  const FIELD_ID_BY_KEY: Record<string, string> =
    checkout?.moeda === MoedaVendaEnum.BRL
      ? {
          fullName: 'name',
          email: 'email',
          phoneNumber: 'phoneNumber',
          cpf: 'cpf',
          cardName: 'mp-core-cardName',
          cardNumber: 'mp-core-cardNumber',
          cardExpiry: 'mp-core-expiry',
          cardCvv: 'mp-core-cvv',
          postalCode: 'postalCode',
          state: 'state',
          city: 'city',
          neighborhood: 'neighborhood',
          address: 'address',
          number: 'number',
        }
      : {
          fullName: 'name',
          email: 'email',
          phoneNumber: 'phoneNumber',
          cpf: 'cpf',
          cardName: 'cardName',
          cardNumber: 'stripe-card-number-container',
          cardExpiry: 'stripe-expiry-container',
          cardCvv: 'stripe-cvc-container',
          postalCode: 'postalCode',
          state: 'state',
          city: 'city',
          neighborhood: 'neighborhood',
          address: 'address',
          number: 'number',
        };

  const [formData, setFormData] =
    React.useState<FormDataShape>(initialFormData);

  const autofillAppliedRef = React.useRef(false);

  const handlePurchaseClick = useCallback(async () => {
    try {
      if (!validateAndFocus()) {
        alert.error(t('pleaseFillRequiredFields'), { timeout: 4000 });
        return;
      }
      if (paymentMethod !== MetodoPagamentoVendaEnum.CARTAO_CREDITO) {
        onSubmit({ ...formData, card: null });
        return;
      }

      if (formData.fullName && formData.fullName.length > 80) {
        alert.error(t('nameMustBeLessThan', { max: 80 }), {
          timeout: 4000,
        });

        const id = FIELD_ID_BY_KEY['fullName'];
        if (id) scrollAndFocus(id);

        if (onValidationError) {
          onValidationError('fullName');
        }

        return;
      }

      if (formData.email && formData.email.length > 60) {
        alert.error(t('emailMustBeLessThan', { max: 60 }), {
          timeout: 4000,
        });

        const id = FIELD_ID_BY_KEY['email'];
        if (id) scrollAndFocus(id);

        if (onValidationError) {
          onValidationError('email');
        }

        return;
      }

      if (checkout?.moeda === MoedaVendaEnum.BRL) {
        if (mpCardRef?.current) {
          const mpPayload = await mpCardRef.current.createToken();

          const contract: CreatePurchaseContract = {
            ...formData,
            card: {
              gateway: 'mercadopago',
              token: mpPayload?.token,
              installments: mpPayload?.installments ?? 1,
              payment_method_id: mpPayload?.payment_method_id ?? null,
              issuer_id: mpPayload?.issuer_id ?? null,
            } as any,
          };

          onSubmit(contract);
          return;
        }
      }

      if (!stripe || !elements) {
        alert.error(t('unexpectedError'), { timeout: 4000 });
        return;
      }

      const cardEl = elements.getElement(CardNumberElement);
      if (!cardEl) {
        alert.error(t('unexpectedError'), { timeout: 4000 });
        return;
      }

      const { token, error } = await stripe.createToken(cardEl, {
        name: formData.cardName || undefined,
      });

      if (error || !token) {
        alert.error(error?.message || t('unexpectedError'), { timeout: 4000 });
        return;
      }

      const contract: CreatePurchaseContract = {
        ...formData,
        card: {
          gateway: 'stripe',
          token: token.id,
          installments: 1,
          payment_method_id: null,
          issuer_id: null,
        } as any,
      };

      onSubmit(contract);
    } catch (err: any) {
      console.error(err);
    }
  }, [
    mpCardRef,
    formData,
    checkout,
    paymentMethod,
    stripeCvcRef,
    stripeExpiryRef,
    stripeNumberRef,
    stripeState,
  ]);

  const handleChange = React.useCallback(
    (field: keyof FormDataShape, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      dispatch(
        setFormField({
          key: field,
          value: value,
        }),
      );
    },
    [],
  );

  const scrollAndFocus = (id: string) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => el.focus?.(), 250);
  };

  const scrollAndFocusStripe = (
    key: 'cardNumber' | 'cardExpiry' | 'cardCvv',
  ) => {
    const id = FIELD_ID_BY_KEY[key];
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const refMap: Record<
      string,
      React.RefObject<{ focus: () => void }> | undefined
    > = {
      cardNumber: stripeNumberRef,
      cardExpiry: stripeExpiryRef,
      cardCvv: stripeCvcRef,
    };
    const ref = refMap[key];
    setTimeout(() => ref?.current?.focus(), 250);
  };

  const validateAndFocus = (): boolean => {
    const requiredKeysBase: Array<keyof typeof formData> = [
      'fullName',
      'email',
      'phoneNumber',
      'cpf',
      ...(checkout?.exibirEndereco
        ? ([
            'postalCode',
            'state',
            'city',
            'neighborhood',
            'address',
            'number',
          ] as Array<keyof typeof formData>)
        : []),
    ];

    const requiredKeys: Array<keyof typeof formData> =
      paymentMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO
        ? checkout?.moeda === MoedaVendaEnum.BRL && !checkout?.recorrencia
          ? ([
              ...requiredKeysBase,
              'cardNumber',
              'cardName',
              'cardExpiry',
              'cardCvv',
            ] as Array<keyof typeof formData>)
          : ['cardName']
        : requiredKeysBase;

    const firstEmptyKey = requiredKeys.find((k) => {
      const v = (formData as any)[k];
      return v === undefined || v === null || String(v).trim() === '';
    });

    if (firstEmptyKey) {
      const id = FIELD_ID_BY_KEY[firstEmptyKey as string];
      if (id) scrollAndFocus(id);
      if (onValidationError) {
        onValidationError(firstEmptyKey);
      }
      return false;
    }
    const isStripeFlow =
      paymentMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO &&
      checkout?.moeda !== MoedaVendaEnum.BRL;

    if (isStripeFlow && stripeState) {
      if (stripeState.number.error) {
        scrollAndFocusStripe('cardNumber');
        if (onValidationError) onValidationError('cardNumber');
        return false;
      }
      if (stripeState.expiry.error) {
        scrollAndFocusStripe('cardExpiry');
        if (onValidationError) onValidationError('cardExpiry');
        return false;
      }
      if (stripeState.cvc.error) {
        scrollAndFocusStripe('cardCvv');
        if (onValidationError) onValidationError('cardCvv');
        return false;
      }

      if (!stripeState.number.complete) {
        scrollAndFocusStripe('cardNumber');
        if (onValidationError) onValidationError('cardNumber');
        return false;
      }
      if (!stripeState.expiry.complete) {
        scrollAndFocusStripe('cardExpiry');
        if (onValidationError) onValidationError('cardExpiry');
        return false;
      }
      if (!stripeState.cvc.complete) {
        scrollAndFocusStripe('cardCvv');
        if (onValidationError) onValidationError('cardCvv');
        return false;
      }
    }

    return true;
  };

  const debounced = useDebounce(
    formData,
    (v) => ({
      email: v.email,
      fullName: v.fullName,
      phoneNumber: v.phoneNumber,
    }),
    3000,
  );

  useEffect(() => {
    if (
      debounced?.email &&
      debounced.email.length > 0 &&
      debounced?.fullName &&
      debounced.fullName.length > 0 &&
      debounced?.phoneNumber &&
      debounced.phoneNumber.length > 0 &&
      eventId &&
      eventId.length > 0
    ) {
      emitFacebookEvent({
        event: 'InitiateCheckout',
        eventId,
        checkout,
        data: debounced as any,
      });
    }
  }, [debounced, eventId]);

  /**
   * Trackeamento para webhook de carrinho abandonado.
   * Não depende de pixel: basta o comprador preencher email OU telefone.
   * O hook faz debounce próprio (~600ms) e re-agenda no microserviço a
   * cada atualização — se ele fechar o checkout sem comprar, o webhook
   * dispara após o `tempoCarrinhoAbandonado` configurado no produto.
   */
  const { track: trackCarrinhoAbandonado } = useCarrinhoAbandonadoTracking(
    checkout?.id,
  );

  useEffect(() => {
    if (!checkout?.id) return;
    const [nome, ...resto] = (debounced?.fullName ?? '').trim().split(/\s+/);
    const sobrenome = resto.join(' ') || undefined;
    trackCarrinhoAbandonado({
      nome: nome || undefined,
      sobrenome,
      email: debounced?.email?.trim() || undefined,
      telefone: debounced?.phoneNumber?.trim() || undefined,
      doc1: formData.cpf?.trim() || undefined,
      cep: formData.postalCode?.trim() || undefined,
      estado: formData.state?.trim() || undefined,
      cidade: formData.city?.trim() || undefined,
      bairro: formData.neighborhood?.trim() || undefined,
      rua: formData.address?.trim() || undefined,
      ultimoEvento: 'InitiateCheckout',
      urlParams: getAllUrlParams(),
    });
  }, [
    debounced?.email,
    debounced?.phoneNumber,
    debounced?.fullName,
    formData.cpf,
    formData.postalCode,
    formData.state,
    formData.city,
    formData.neighborhood,
    formData.address,
    checkout?.id,
    trackCarrinhoAbandonado,
  ]);

  const isContactValid = Boolean(
    formData.email && formData.fullName && formData.phoneNumber,
  );
  const isDeliveryValid = Boolean(
    formData.postalCode &&
      formData.state &&
      formData.city &&
      formData.neighborhood &&
      formData.address &&
      formData.number,
  );

  const validate = React.useCallback(() => {
    const required = checkout?.exibirEndereco
      ? [
          'email',
          'fullName',
          'cpf',
          'postalCode',
          'state',
          'city',
          'neighborhood',
          'address',
          'number',
        ]
      : ['email', 'fullName', 'cpf'];

    const ok = required.every((k) => (formData as any)[k]);
    if (!ok) {
      alert.error(t('pleaseFillRequiredFields'), { timeout: 4000 });
      return false;
    }

    if (!paymentMethod) {
      alert.error(t('pleaseSelectPaymentMethod'), { timeout: 4000 });
      return false;
    }

    return true;
  }, [alert, t, checkout?.exibirEndereco, formData, paymentMethod]);

  useEffect(() => {
    if (form) {
      setFormData((prev) => ({ ...form, cardName: prev.cardName }));
    }
  }, [form]);

  useEffect(() => {
    if (autofillAppliedRef.current || !checkout?.id) return;
    autofillAppliedRef.current = true;

    const autofill = parseAutofillParams();
    const ADDRESS_FIELDS: ReadonlyArray<keyof FormDataShape> = [
      'postalCode',
      'state',
      'city',
      'neighborhood',
      'address',
      'number',
      'complement',
    ];

    (Object.entries(autofill) as Array<[keyof FormDataShape, string]>).forEach(
      ([key, value]) => {
        if (ADDRESS_FIELDS.includes(key) && !checkout.exibirEndereco) return;
        if (!form[key]) {
          dispatch(setFormField({ key, value }));
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout?.id, checkout?.exibirEndereco]);

  return {
    formData,
    setFormData,
    handleChange,
    isContactValid,
    isDeliveryValid,
    validate,
    handlePurchaseClick,
  };
}
